/**
 * Activates server-rendered text/plain script placeholders when their
 * category is granted. Replaces the node so browsers (re)execute the script.
 */

const SELECTOR = 'script[type="text/plain"][data-consent-category]';

export default class ScriptLoader {
	constructor(store) {
		this.store = store;
		this.activated = new WeakSet();
		this.queue = Promise.resolve();
	}

	init() {
		this.activateGranted();
		this.store.on('changed', () => this.activateGranted());
	}

	activateGranted() {
		const granted = Array.from(document.querySelectorAll(SELECTOR)).filter((node) => {
			if (this.activated.has(node)) return false;
			return this.store.has(node.dataset.consentCategory);
		});

		// Reserve the batch before it enters the promise chain. A second consent
		// event can arrive while an external script is loading; marking now keeps
		// that event from queuing the same placeholders twice.
		granted.forEach((node) => this.activated.add(node));

		this.queue = this.queue.then(async () => {
			for (const node of granted) {
				if (node.isConnected) await this._activate(node);
			}
		});

		return this.queue;
	}

	_activate(plainNode) {
		const real = document.createElement('script');
		// Copy all attributes EXCEPT type which we force to JS.
		for (const attr of plainNode.attributes) {
			if (attr.name === 'type') continue;
			real.setAttribute(attr.name, attr.value);
		}
		real.type = 'text/javascript';
		let ready = Promise.resolve();
		if (plainNode.src) {
			// Dynamically inserted classic scripts are async by default. Disabling
			// async preserves external-to-external order; awaiting load/error also
			// keeps a following inline snippet behind the library it depends on.
			real.async = false;
			ready = new Promise((resolve) => {
				real.addEventListener('load', resolve, {once: true});
				real.addEventListener('error', resolve, {once: true});
			});
		} else {
			real.text = plainNode.textContent || '';
		}
		plainNode.parentNode.replaceChild(real, plainNode);
		return ready;
	}
}
